import * as d3 from 'd3';
import { CandleDataPoint, AggregatedVolumePoint, VolumeVisibility } from '../../types';
import { RenderContext } from '../types';
import { VOLUME_COLORS } from '../constants';
import { detectFrequency } from '../../candleUtils';
import { calculateBarDimensions } from '../utils/barDimensions';

interface VolumePanelConfig {
    candles: CandleDataPoint[];
    aggregatedVolume: AggregatedVolumePoint[];
    volumeVisibility: VolumeVisibility;
}

export function renderVolumePanel(ctx: RenderContext, config: VolumePanelConfig): void {
    const { g, scales, layout, clipId } = ctx;
    const { candles, aggregatedVolume, volumeVisibility } = config;

    if (!layout.showVolume || !scales.yVolume || candles.length === 0) return;

    // Create volume group positioned below the price chart with clipping
    const volumeGroup = g.append('g')
        .attr('class', 'volume-panel')
        .attr('transform', `translate(0, ${layout.volumeYOffset})`)
        .attr('clip-path', `url(#${clipId})`);

    // Calculate bar dimensions based on candle time duration
    const frequencyMs = ctx.candleFrequencyMs ?? detectFrequency(candles).frequencyMs;
    const { widthFromScale: barWidthFromScale, width: barWidth, offset: barOffset } = calculateBarDimensions(scales.x, frequencyMs);

    if (volumeVisibility.metric === 'buySellVolume') {
        renderBuySellVolumeBars(volumeGroup, {
            scales,
            layout,
            aggregatedVolume,
            barWidthFromScale,
            barWidth,
            barOffset,
        });
    } else {
        renderSimpleVolumeBars(volumeGroup, {
            scales,
            layout,
            candles,
            volumeVisibility,
            barWidthFromScale,
            barWidth,
            barOffset,
        });
    }
}

interface BuySellBarsConfig {
    scales: RenderContext['scales'];
    layout: RenderContext['layout'];
    aggregatedVolume: AggregatedVolumePoint[];
    barWidthFromScale: number;
    barWidth: number;
    barOffset: number;
}

function renderBuySellVolumeBars(
    volumeGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
    config: BuySellBarsConfig
): void {
    const { scales, layout, aggregatedVolume, barWidthFromScale, barWidth, barOffset } = config;

    aggregatedVolume.forEach(point => {
        const xPos = scales.x(point.timestamp);
        // Skip bars completely outside visible area
        if (xPos + barWidthFromScale < 0 || xPos > layout.innerWidth) return;

        const totalValue = point.buyVolume + point.sellVolume;
        if (totalValue <= 0) return;

        const totalBarHeight = layout.volumeHeight - scales.yVolume!(totalValue);
        const sellBarHeight = totalValue > 0 ? (point.sellVolume / totalValue) * totalBarHeight : 0;
        const buyBarHeight = totalBarHeight - sellBarHeight;

        // Sell volume (bottom, red)
        if (point.sellVolume > 0) {
            volumeGroup.append('rect')
                .attr('class', 'volume-bar-sell')
                .attr('x', xPos + barOffset)
                .attr('y', layout.volumeHeight - sellBarHeight)
                .attr('width', barWidth)
                .attr('height', sellBarHeight)
                .attr('fill', VOLUME_COLORS.volumeDown)
                .style('pointer-events', 'none');
        }

        // Buy volume (stacked on top, green)
        if (point.buyVolume > 0) {
            volumeGroup.append('rect')
                .attr('class', 'volume-bar-buy')
                .attr('x', xPos + barOffset)
                .attr('y', layout.volumeHeight - totalBarHeight)
                .attr('width', barWidth)
                .attr('height', buyBarHeight)
                .attr('fill', VOLUME_COLORS.volumeUp)
                .style('pointer-events', 'none');
        }
    });
}

interface SimpleBarsConfig {
    scales: RenderContext['scales'];
    layout: RenderContext['layout'];
    candles: CandleDataPoint[];
    volumeVisibility: VolumeVisibility;
    barWidthFromScale: number;
    barWidth: number;
    barOffset: number;
}

function renderSimpleVolumeBars(
    volumeGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
    config: SimpleBarsConfig
): void {
    const { scales, layout, candles, volumeVisibility, barWidthFromScale, barWidth, barOffset } = config;

    candles.forEach(candle => {
        const xPos = scales.x(candle.timestamp);
        // Skip bars completely outside visible area
        if (xPos + barWidthFromScale < 0 || xPos > layout.innerWidth) return;

        // Get value based on selected metric
        const value = volumeVisibility.metric === 'volume'
            ? candle.baseVolume
            : candle.tradeCount;

        if (value <= 0) return;

        // Color based on price direction
        const isUp = candle.close >= candle.open;
        const color = isUp ? VOLUME_COLORS.volumeUp : VOLUME_COLORS.volumeDown;

        volumeGroup.append('rect')
            .attr('class', 'volume-bar')
            .attr('x', xPos + barOffset)
            .attr('y', scales.yVolume!(value))
            .attr('width', barWidth)
            .attr('height', layout.volumeHeight - scales.yVolume!(value))
            .attr('fill', color)
            .attr('opacity', candle.isFilled ? 0.3 : 1)
            .style('pointer-events', 'none');
    });
}
